import * as msal from '@azure/msal-browser';
import router from '@/router/index';

export const msalMixin = {
  data() {
    return {
      msal: this.$msal ? this.$msal.data : {},
    };
  },
  created() {
    this.$watch(
      '$msal.data',
      (value) => {
        this.msal = value;
      },
      { deep: true }
    );
  },
};

class MSAL {
  lib;
  tokenExpirationTimers = {};
  data = {
    isAuthenticated: false,
    accessToken: '',
    user: {},
  };
  auth = {
    clientId: '',
    authority: '',
    redirectUri: window.location.href,
    postLogoutRedirectUri: window.location.href,
    navigateToLoginRequestUrl: true,
  };
  cache = {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true,
  };
  request = {
    scopes: ['user.read'],
  };
  constructor(options) {
    if (!options.auth.clientId) {
      throw new Error('auth.clientId is required');
    }
    this.auth = Object.assign(this.auth, options.auth);
    this.cache = Object.assign(this.cache, options.cache);
    this.request = Object.assign(this.request, options.request);

    this.lib = new msal.PublicClientApplication({
      auth: {
        clientId: this.auth.clientId,
        authority: this.auth.authority,
        redirectUri: this.auth.redirectUri,
        postLogoutRedirectUri: this.auth.postLogoutRedirectUri,
        navigateToLoginRequestUrl: this.auth.navigateToLoginRequestUrl,
      },
      cache: this.cache,
      system: options.system,
    });

    // リダイレクト後に呼ばれる
    this.lib
      .handleRedirectPromise()
      .then((response) => {
        if (!response) {
          return;
        }
        this.handleResponse(null, response);

        // vue-routerでセッションストレージに保存した
        // ログイン画面に戻させる前のURLを取り出し、遷移を行う
        const prevPath = JSON.parse(sessionStorage.getItem('prevPath'));
        sessionStorage.removeItem('prevPath');
        if (prevPath) {
          router.push({ name: prevPath.name, params: prevPath.params });
        } else {
          router.push({ name: 'Root' });
        }
      })
      .catch((error) => console.log(error));

    // 既にログインしている場合は、アクティブユーザをセットして
    // アクセストークンを取得する
    const accounts = this.lib.getAllAccounts();
    if (accounts.length > 0) {
      this.lib.setActiveAccount(accounts[0]);
      this.data.user = this.lib.getActiveAccount();
    }

    this.data.isAuthenticated = this.isAuthenticated();
    if (this.data.isAuthenticated) {
      this.acquireToken();
    }
  }
  // サインイン
  signIn() {
    if (!this.lib.getActiveAccount()) {
      this.lib.loginRedirect(this.request);
    }
  }
  // サインアウト
  async signOut() {
    this.lib.logoutRedirect();
  }
  // ログイン済みか判定、アクティブなユーザーがいればTrue
  isAuthenticated() {
    return this.lib.getActiveAccount() !== null;
  }
  // トークン要求
  async acquireToken(request = this.request, retries = 0) {
    try {
      // 画面なしでトークン供給
      const response = await this.lib.acquireTokenSilent(request);
      this.handleTokenResponse(null, response);
      return response;
    } catch (error) {
      if (error instanceof msal.InteractionRequiredAuthError) {
        // 画面からの認証に失敗したら、リダイレクトで認証を行う
        this.lib.acquireTokenRedirect(request);
      } else if (retries > 0) {
        // リトライ機能
        return await new Promise((resolve) => {
          setTimeout(async () => {
            const res = await this.acquireToken(request, retries - 1);
            resolve(res);
          }, 60 * 1000);
        });
      }
      return false;
    }
  }
  // レスポンス処理
  handleResponse(error, response) {
    if (error) {
      return;
    }
    this.lib.setActiveAccount(response.account);
    this.data.user = this.lib.getActiveAccount();
    this.handleTokenResponse(null, response);
  }
  // レスポンスからトークンをセットする
  handleTokenResponse(error, response) {
    if (error) {
      return;
    }
    if (this.data.accessToken !== response.accessToken) {
      this.setToken('accessToken', response.accessToken, response.expiresOn, response.scopes);
    }
  }
  // dataにトークンをセット
  setToken(tokenType, token, expiresOn, scopes) {
    // const expirationOffset = this.lib.config.system.tokenRenewalOffsetSeconds * 1000
    // const expiration = expiresOn.getTime() - (new Date()).getTime() - expirationOffset
    const expiration = expiresOn.getTime() - new Date().getTime();
    if (expiration >= 0) {
      this.data[tokenType] = token;
    }
    // トークンのオートリフレッシュ
    if (this.tokenExpirationTimers[tokenType]) clearTimeout(this.tokenExpirationTimers[tokenType]);
    this.tokenExpirationTimers[tokenType] = window.setTimeout(async () => {
      await this.acquireToken({ scopes }, 3);
    }, expiration);
  }
}

// プラグイン
export default class msalPlugin {
  static install(Vue, options) {
    Vue.prototype.$msal = new msalPlugin(options, Vue);
  }
  constructor(options, Vue = undefined) {
    const msal = new MSAL(options);
    if (Vue && options.framework && options.framework.globalMixin) {
      Vue.mixin(msalMixin);
    }
    const exposed = {
      data: msal.data,
      signIn() {
        msal.signIn();
      },
      async signOut() {
        await msal.signOut();
      },
      isAuthenticated() {
        return msal.isAuthenticated();
      },
    };
    return exposed;
  }
}
